import api from '@/services/api.service';
import type { ReportQueryResult } from '@/reports/types';

export type ReportSavedView = {
  id: string;
  report_id: string;
  name: string;
  is_default: boolean;
  is_shared: boolean;
  filters_json: Record<string, unknown> | null;
  columns_json: Record<string, unknown> | null;
  sort_json: Record<string, unknown> | null;
};

export type ReportSchedule = {
  id: string;
  report_id: string;
  name: string;
  cron_expression: string;
  export_format: string;
  filters_json: Record<string, unknown> | null;
  recipients_json: string[] | null;
  is_active: boolean;
};

function unwrap<T>(res: any): T {
  return (res?.data?.data ?? res?.data) as T;
}

export const reportsService = {
  async listCatalog(params?: { module?: string; q?: string; packs?: string }) {
    const res = await api.get('/reports/catalog', { params });
    return unwrap<any[]>(res) || [];
  },

  async queryReport(dataSource: string, params: Record<string, unknown>): Promise<ReportQueryResult> {
    const res = await api.get(dataSource, { params });
    const raw = unwrap<any>(res);
    if (Array.isArray(raw)) {
      return { rows: raw, total: raw.length, page: 1, pageSize: raw.length || 50 };
    }
    return {
      rows: raw?.rows || [],
      total: raw?.total ?? 0,
      totals: raw?.totals,
      grandTotals: raw?.grandTotals,
      page: raw?.page ?? 1,
      pageSize: raw?.pageSize ?? 50,
    };
  },

  async listSavedViews(reportId: string): Promise<ReportSavedView[]> {
    const res = await api.get('/reports/saved-views', { params: { reportId } });
    return unwrap<ReportSavedView[]>(res) || [];
  },

  async createSavedView(body: Partial<ReportSavedView> & { report_id: string; name: string }) {
    const res = await api.post('/reports/saved-views', body);
    return unwrap<ReportSavedView>(res);
  },

  async updateSavedView(id: string, body: Partial<ReportSavedView>) {
    const res = await api.patch(`/reports/saved-views/${id}`, body);
    return unwrap<ReportSavedView>(res);
  },

  async deleteSavedView(id: string) {
    await api.delete(`/reports/saved-views/${id}`);
  },

  async logRun(body: {
    reportId: string;
    action: 'view' | 'print' | 'export' | 'schedule';
    filters?: Record<string, unknown>;
    rowCount?: number;
    exportFormat?: string;
  }) {
    await api.post('/reports/runs', body);
  },

  async listAudit(params?: { reportId?: string; limit?: number }) {
    const res = await api.get('/reports/audit', { params });
    return unwrap<any[]>(res) || [];
  },

  async enqueueExport(body: {
    reportId: string;
    format: 'csv' | 'xlsx';
    filters?: Record<string, unknown>;
    sort?: Record<string, unknown>;
  }) {
    const res = await api.post('/reports/export-jobs', body);
    return unwrap<any>(res);
  },

  async listSchedules(reportId?: string): Promise<ReportSchedule[]> {
    const res = await api.get('/reports/schedules', { params: reportId ? { reportId } : undefined });
    return unwrap<ReportSchedule[]>(res) || [];
  },

  async createSchedule(body: {
    report_id: string;
    name: string;
    cron_expression: string;
    export_format?: string;
    filters_json?: Record<string, unknown> | null;
    recipients_json?: string[] | null;
  }) {
    const res = await api.post('/reports/schedules', body);
    return unwrap<ReportSchedule>(res);
  },

  async deleteSchedule(id: string) {
    await api.delete(`/reports/schedules/${id}`);
  },
};

export default reportsService;
